import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Button } from "@/components/design/Button";
import { ShareDetailStatus } from "@/components/share/ShareDetailStatus";
import {
  ListingApplicationState,
  MyListingApplicationDetailDocument,
  OpenListingDocument,
  SortOrder,
} from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { ListingField } from "@/pages/share/listings/ListingField";
import { ListingPostView } from "@/pages/share/listings/ListingPostView";
import { listingSearchTarget } from "../listings/listing-search-target";
import { ApplyDialog } from "./ApplyDialog";
import { LISTING_APPLICATION_STATE_LABELS } from "./listing-application-state";

// SHARE · Offene Ausschreibung, aus Sicht der Bewerbenden: Inhalt links, Frist und Bewerbung rechts
// (ListingPostView, dieselbe Darstellung wie die Vorschau aus dem Dashboard der Ausschreibung).
// "Bewerben" steht nur da, wo genesis `actions.canApply` meldet; ob die Ausschreibung offen ist, ob
// die Person sie lesen darf und ob sie schon eine laufende Bewerbung hat, rechnet genesis aus, nicht
// die Seite (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §4)).
//
// Hat sie schon eine laufende Bewerbung, sagt die Seite das, statt den Knopf kommentarlos wegzulassen.
// Eine zurückgezogene zählt nicht: nach einem Rückzug darf sie sich neu bewerben.
export function OpenListingPage() {
  const intl = useIntl();
  const { listingId = "" } = useParams();
  // Der Dialog hängt nicht an der geladenen Ausschreibung: nach dem Absenden liest die Seite sie neu,
  // und die Bestätigung soll das überstehen.
  const [applyingTo, setApplyingTo] = useState<{ id: string; name: string } | null>(null);
  const query = useQuery(OpenListingDocument, { variables: { id: listingId }, skip: !listingId });
  const ownInput = useMemo(
    () => ({ where: { listingId: { eq: listingId } }, order: { receivedOn: SortOrder.Desc }, first: 1 }),
    [listingId],
  );
  const own = useQuery(MyListingApplicationDetailDocument, { variables: { input: ownInput }, skip: !listingId });

  const latest = own.data?.myListingApplications.nodes[0];
  const listing = query.data?.openListing ?? latest?.listing ?? null;
  const loading = query.loading || own.loading;
  const current = latest && latest.state !== ListingApplicationState.Withdrawn ? latest : null;

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="OpenListing">
      <div className="mb-5">
        <BackButton />
      </div>
      {!listing ? (
        <ShareDetailStatus
          loading={loading}
          error={query.error ?? own.error}
          notFoundLabel={intl.formatMessage({
            id: "share.openListings.notFound",
            defaultMessage: "Diese Ausschreibung ist nicht offen oder nicht für dich sichtbar",
          })}
        />
      ) : (
        <ListingPostView
          listing={listing}
          fields={
            current ? (
              <ListingField
                label={<FormattedMessage id="share.openListings.yourApplication" defaultMessage="Deine Bewerbung" />}
              >
                <span id="open-listing-own-application">
                  <FormattedMessage
                    id="share.openListings.yourApplicationValue"
                    defaultMessage="{state}, eingegangen am {date}"
                    values={{
                      state: intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[current.state]),
                      date: formatDateTime(current.receivedOn, intl.locale) || "–",
                    }}
                  />
                </span>
              </ListingField>
            ) : null
          }
        >
          {listing.actions.canApply ? (
            <Button id="open-listing-apply" className="mt-5" onClick={() => setApplyingTo({ id: listing.id, name: listing.name ?? "" })}>
              <FormattedMessage id="share.openListings.apply" defaultMessage="Bewerben" />
            </Button>
          ) : null}
          {current ? (
            <Link
              to="/share/applications"
              className="mt-5 inline-flex items-center gap-1 text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink-accent"
            >
              <FormattedMessage id="share.openListings.toMyApplications" defaultMessage="Meine Bewerbungen" />
              <span aria-hidden="true">↗</span>
            </Link>
          ) : null}
        </ListingPostView>
      )}
      {applyingTo ? (
        <ApplyDialog
          listingId={applyingTo.id}
          listingName={applyingTo.name}
          companyApplication={listingSearchTarget(listing?.data) === "COMPANIES"}
          onClose={() => setApplyingTo(null)}
        />
      ) : null}
    </main>
  );
}
